import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Panel, StatusBadge, JobTypeChip } from "@/components/common";
import { FilterSelect } from "@/components/BookingFilters";
import { useSettings } from "@/context/SettingsContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/format";
import { cn } from "@/lib/utils";

const iso = (d) => format(d, "yyyy-MM-dd");
const QUICK = [
  ["This week", () => [startOfWeek(new Date(), { weekStartsOn: 1 }), endOfWeek(new Date(), { weekStartsOn: 1 })]],
  ["Last week", () => [startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 })]],
  ["Last 2 weeks", () => [startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), endOfWeek(new Date(), { weekStartsOn: 1 })]],
  ["This month", () => [startOfMonth(new Date()), endOfMonth(new Date())]],
  ["Last month", () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))]],
];

export const InvoiceBuilder = ({ onCreated }) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [company, setCompany] = useState("");
  const [mode, setMode] = useState("running");
  const [range, setRange] = useState(() => { const [s, e] = QUICK[3][1](); return { start: iso(s), end: iso(e) }; });
  const [jobs, setJobs] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const openRunning = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/running", { company });
      toast.success(`${data.invoice_number} – running account for ${company}, ${data.lines.length} job${data.lines.length > 1 ? "s" : ""}`);
      onCreated?.();
      navigate(`/invoices/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (mode !== "range" || !company || !range.start || !range.end) { setJobs(null); return; }
    let on = true;
    api.get("/invoices/preview", { params: { company, ...range } }).then(({ data }) => {
      if (!on) return;
      setJobs(data); setPicked(new Set(data.map((j) => j.booking_id)));
    }).catch((e) => toast.error(formatApiError(e)));
    return () => { on = false; };
  }, [company, range, mode]);

  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selected = (jobs || []).filter((j) => picked.has(j.booking_id));
  const total = selected.reduce((a, j) => a + (j.net ?? j.amount), 0);
  const collected = selected.reduce((a, j) => a + (j.collected || 0), 0);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/create", { company, ...range, booking_ids: [...picked] });
      toast.success(`${data.invoice_number} created – ${data.lines.length} job${data.lines.length > 1 ? "s" : ""}, ${money(data.subtotal)}`);
      onCreated?.();
      navigate(`/invoices/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Create invoice" className="mb-6" id="invoice-builder">
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="builder-mode">
        {[["running", "Running account – one invoice per company"], ["range", "Date range – separate invoice"]].map(([m, label]) => (
          <button key={m} type="button" onClick={() => setMode(m)} data-testid={`mode-${m}`} className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors", mode === m ? "border-[#0E1E38] bg-[#0E1E38] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400")}>{label}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><div className="text-xs font-medium text-slate-600">Company</div><FilterSelect id="builder-company" placeholder="Company" value={company} options={settings.companies.map((c) => c.name)} onChange={setCompany} /></div>
        {mode === "running" && (
          <Button onClick={openRunning} disabled={busy || !company} data-testid="btn-open-running" className="bg-[#FACC15] font-semibold text-[#0E1E38] hover:bg-[#EAB308]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Open running invoice{company ? ` for ${company}` : ""}
          </Button>
        )}
        {mode === "range" && <>
        <div className="space-y-1"><div className="text-xs font-medium text-slate-600">From</div><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="builder-start" className="h-9 w-[150px] bg-white text-sm" /></div>
        <div className="space-y-1"><div className="text-xs font-medium text-slate-600">To</div><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="builder-end" className="h-9 w-[150px] bg-white text-sm" /></div>
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map(([label, fn]) => {
            const [s, e] = fn(); const active = iso(s) === range.start && iso(e) === range.end;
            return <button key={label} type="button" onClick={() => setRange({ start: iso(s), end: iso(e) })} data-testid={`quick-${label.toLowerCase().replace(/ /g, "-")}`} className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors", active ? "border-[#0E1E38] bg-[#0E1E38] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400")}>{label}</button>;
          })}
        </div>
        </>}
      </div>
      {mode === "running" && <p className="mt-4 text-sm text-slate-500" data-testid="builder-running-hint">Every completed job for the company stacks onto one open invoice until you mark it Sent – then a fresh one starts automatically and you mark the sent one Paid when the money arrives. Use “Date range” only when you need a separate invoice for a specific week or month.</p>}
      {mode === "range" && !company && <p className="mt-4 text-sm text-slate-500" data-testid="builder-hint">Choose a company to see its completed jobs for the selected dates.</p>}
      {mode === "range" && company && jobs && (
        <div className="mt-4">
          {jobs.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 py-8 text-center text-sm text-slate-500" data-testid="builder-empty">No completed, uninvoiced jobs for {company} between {fmtDate(range.start, "d MMM")} and {fmtDate(range.end, "d MMM yyyy")}. Jobs must be marked Completed or Paid to be invoiced.</p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm" data-testid="builder-jobs-table">
                  <thead><tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2"><Checkbox checked={picked.size === jobs.length} onCheckedChange={(v) => setPicked(v ? new Set(jobs.map((j) => j.booking_id)) : new Set())} data-testid="builder-select-all" /></th>
                    {["Date", "Move Pro #", "Customer", "Route", "Pickup → Delivery", "Status", "Amount"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {jobs.map((j) => (
                      <tr key={j.booking_id} onClick={() => toggle(j.booking_id)} data-testid={`builder-job-${j.booking_id}`} className={cn("cursor-pointer border-t border-slate-100", picked.has(j.booking_id) ? "bg-white" : "bg-slate-50/70 text-slate-400")}>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><Checkbox checked={picked.has(j.booking_id)} onCheckedChange={() => toggle(j.booking_id)} data-testid={`builder-check-${j.booking_id}`} /></td>
                        <td className="whitespace-nowrap px-3 py-2">{fmtDate(j.moving_date, "EEE d MMM")}</td>
                        <td className="px-3 py-2 font-mono font-semibold">{j.job_number}</td>
                        <td className="px-3 py-2 font-medium">{j.customer_name}</td>
                        <td className="px-3 py-2"><JobTypeChip name={j.job_type} /></td>
                        <td className="max-w-[260px] truncate px-3 py-2 text-slate-500" title={`${j.pickup_address} → ${j.delivery_address}`}>{j.pickup_address} → {j.delivery_address}</td>
                        <td className="px-3 py-2"><StatusBadge status={j.status} /></td>
                        <td className="whitespace-nowrap px-3 py-2 text-right font-mono font-semibold">{money(j.amount)}{j.collected ? <div className="text-[10px] font-medium text-rose-700" data-testid={`builder-collected-${j.booking_id}`}>− {money(j.collected)} collected by us</div> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-600" data-testid="builder-summary"><span className="font-semibold text-slate-900">{selected.length}</span> of {jobs.length} jobs selected{collected > 0 && <span className="text-rose-700"> · less {money(collected)} collected by us</span>} · {total >= 0 ? "Invoice total" : `Credit – we owe ${company}`} <span className={cn("font-mono text-base font-bold", total < 0 ? "text-rose-700" : "text-[#0E1E38]")} data-testid="builder-total">{money(Math.abs(total))}</span></div>
                <Button onClick={create} disabled={busy || !selected.length} data-testid="btn-create-invoice" className="bg-[#FACC15] font-semibold text-[#0E1E38] hover:bg-[#EAB308]">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Create invoice for {company}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Panel>
  );
};
